import React, { FC, Fragment, memo, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import TicketRoute from './TicketRoute';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import TicketRouteGds from './TicketRouteGds';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowRight, faPlane } from '@fortawesome/free-solid-svg-icons';
import { HighlightRoute } from 'ts/flight/interfaces/HighlightRoute';
import { useTranslation } from 'react-i18next';
import { FlightsResultContext } from 'contexts/FlightsResultContext';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { RoundTripMode } from 'constants/flight';
import { animateBound } from 'utils/flight/flight';
import { MainContext } from 'contexts/MainContext';
import { isTT } from 'constants/generic';
import {
  clearLocalFlightCardAction,
  getLocalFlightCardAction,
  subscribeToLocalFlightCardAction,
} from 'utils/assistantChatStore';

interface FlightTicketProps {
  flight: FlightSearch;
  sessionStorageKey: string;
}

const FlightTicket: FC<FlightTicketProps> = ({ flight, sessionStorageKey }) => {
  const [open, setOpen] = useState(false);
  const [t] = useTranslation();
  const [highlightedRoutes, setHighlightedRoutes] = useState<HighlightRoute[]>([]);
  const [highlightedGds, setHighlightedGds] = useState<HighlightRoute[]>([]);
  const higlightedGdsOutbound = highlightedGds.filter((gds) => gds.isOutbound);
  const higlightedGdsInbound = highlightedGds.filter((gds) => !gds.isOutbound);
  const peopleCount = flight.flightPricesByPDT.reduce((acc, pdt) => pdt.quantity + acc, 0);
  const flightContext = useContext(FlightsResultContext);
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const priceObj = flight.flighPrice.grossAndTaxWithMarkup;
  const { setProposal, proposal } = useContext(MainContext);
  const ticketRef = useRef<HTMLDivElement>(null);
  const localCardAction = useSyncExternalStore(
    subscribeToLocalFlightCardAction,
    getLocalFlightCardAction,
  );

  useEffect(() => {
    if (!localCardAction || localCardAction.kind !== 'info') {
      return;
    }
    if (localCardAction.flightId !== flight.id) {
      return;
    }
    if (localCardAction.show === true) {
      setOpen(true);
    } else if (localCardAction.show === false) {
      setOpen(false);
    } else {
      setOpen((prev) => !prev);
    }
    clearLocalFlightCardAction();
  }, [localCardAction, flight.id]);

  useEffect(() => {
    if (flight.inbound) {
      const arr: HighlightRoute[] = [flight.outbound, flight.inbound].map(() => ({
        departure: false,
        arrival: false,
      }));

      if (flight.outbound.arrivalAirport.code !== flight.inbound.departureAirport.code) {
        arr[0].arrival = true;
        arr[1].departure = true;
      }

      if (flight.outbound.gdsFlyAirs?.length && flight.inbound.gdsFlyAirs?.length) {
        const gdsArr = [...flight.outbound.gdsFlyAirs, ...flight.inbound.gdsFlyAirs].map(
          (fly, index) => ({
            departure: false,
            arrival: false,
            departureAirport: fly.departureAirport.code,
            arrivalAirport: fly.arrivalAirport.code,
            isOutbound: flight.outbound.gdsFlyAirs.length > index,
          })
        );

        for (let i = 0; i < gdsArr.length; i++) {
          if (!gdsArr[i + 1]) break;
          if (gdsArr[i].arrivalAirport !== gdsArr[i + 1].departureAirport) {
            gdsArr[i].arrival = true;
            gdsArr[i + 1].departure = true;
          }
        }

        setHighlightedGds(gdsArr);
      }

      setHighlightedRoutes(arr);
    } else if (flight.outbound) {
      const arr = [{ departure: false, arrival: false }];
      if (flight.outbound.gdsFlyAirs?.length) {
        const gdsArr = flight.outbound.gdsFlyAirs.map((fly) => ({
          departure: false,
          arrival: false,
          departureAirport: fly.departureAirport.code,
          arrivalAirport: fly.arrivalAirport.code,
        }));

        for (let i = 0; i < gdsArr.length; i++) {
          if (!gdsArr[i + 1]) break;
          if (gdsArr[i].arrivalAirport !== gdsArr[i + 1].departureAirport) {
            gdsArr[i].arrival = true;
            gdsArr[i + 1].departure = true;
          }
        }

        setHighlightedGds(gdsArr);
      }

      setHighlightedRoutes(arr);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = () => {
    if (flightContext?.roundTripMode !== RoundTripMode.Default) {
      animateBound(
        ticketRef.current,
        !!flightContext?.inboundFlight || !!flightContext?.outboundFlight
      );
    }
    flightContext?.onClickChoose(flight, sessionStorageKey);
  };

  const onClickProposal = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();

    if (!proposal || !setProposal) return;
    const existingProposalIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === flight.id
    );

    if (existingProposalIndex !== -1) {
      const updatedFlights = proposal.flights.filter((_, index) => index !== existingProposalIndex);
      setProposal({ ...proposal, flights: updatedFlights });
    } else {
      const newProposal = {
        item: JSON.parse(JSON.stringify(flight)),
        currency: priceObj.currency,
        price: priceObj.value,
        displayPrice: true,
        searchToken: sessionStorage.getItem(sessionStorageKey) || '',
      };

      setProposal({ ...proposal, flights: [...proposal.flights, newProposal] });
    }
  };

  return (
    <div
      id={`flight-card-${flight.id}`}
      className="ticket default-shadow position-relative"
      ref={ticketRef}
      data-assistant-target="list"
    >
      <ProposalHeart
        active={!!proposal?.flights.some((f) => f.item.id === flight.id)}
        onClickPropsal={onClickProposal}
      />
      <div className="ticket-source">
        <span>{t(flight.flightSource)}</span>
      </div>
      <div className="ticket-routes">
        {flight.outbound && (
          <TicketRoute
            outbond={flight.outbound}
            highlighted={highlightedRoutes[0]}
            setOpen={() => setOpen(!open)}
            firstRoute={true}
            choose={choose}
          />
        )}
        {flight.inbound && (
          <TicketRoute
            outbond={flight.inbound}
            highlighted={highlightedRoutes[1]}
            setOpen={() => setOpen(!open)}
            firstRoute={false}
            choose={choose}
          />
        )}
        {flight.flightBounds?.map((bound, index) => (
          <TicketRoute
            outbond={bound}
            highlighted={highlightedRoutes[1]}
            key={index}
            setOpen={() => setOpen(!open)}
            firstRoute={index === 0}
            choose={choose}
          />
        ))}

        {open && (
          <div className="ticket-long">
            <div className="mb-20">
              {!!flight.outbound && (
                <Fragment>
                  {!!flight.inbound && (
                    <span className="text-sm">
                      {' '}
                      <FontAwesomeIcon icon={faPlane as any} /> {t('flight.forward')}
                    </span>
                  )}

                  {flight.outbound.gdsFlyAirs?.map((_, index) => (
                    <TicketRouteGds
                      outbond={flight.outbound}
                      gdsIndex={index}
                      highlighted={higlightedGdsOutbound[index]}
                      choose={choose}
                    />
                  ))}
                </Fragment>
              )}
              {flight.flightBounds?.map((bound, index) => (
                <Fragment key={index}>
                  <span className="text-sm">
                    <FontAwesomeIcon icon={faPlane as any} /> {t('flight.flight')} {index + 1}
                  </span>

                  {bound.gdsFlyAirs?.map((_, index) => (
                    <TicketRouteGds
                      outbond={bound}
                      gdsIndex={index}
                      highlighted={higlightedGdsOutbound[index]}
                      choose={choose}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
            <div>
              {!!flight.inbound && (
                <span className="text-sm return">
                  <FontAwesomeIcon icon={faPlane as any} />
                  {t('flight.returning')}
                </span>
              )}
              {flight.inbound?.gdsFlyAirs?.map((_, index) => (
                <TicketRouteGds
                  outbond={flight.inbound}
                  gdsIndex={index}
                  highlighted={higlightedGdsInbound[index]}
                  choose={choose}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="ticket-price">
        {peopleCount > 1 && (
          <span className="price-passenger">
            <span>{t('flight.avarage_per_passenger')}:</span>
            <span className="price">
              {formatPriceCurrency(priceObj.value / peopleCount, getCurrency(priceObj.currency))}
            </span>
          </span>
        )}

        <span className="total-price">
          <span>{t('flight.total_price')}:</span>
          <span className="price mr-5 mb-20" onClick={() => isTT && choose()}>
            {formatPriceCurrency(priceObj.value, getCurrency(priceObj.currency))}
          </span>

          <FontAwesomeIcon
            className="cur-poi"
            icon={faCircleArrowRight as any}
            color={colorContrast}
            size="xl"
            onClick={() => choose()}
          />

          {!isTT && flight.flightSource?.includes('ALP') && (
            <div className="mt-10 text-sm text-black">{flight.provider}</div>
          )}
        </span>

        {flight.outbound?.gdsFlyAirs?.length &&
          flight.outbound.gdsFlyAirs[0].availSeatsNo < 5 &&
          flight.outbound.gdsFlyAirs[0].availSeatsNo > 0 && (
            <span className="available-seats">
              {flight.outbound.gdsFlyAirs[0].availSeatsNo} {t('flight.seats_available')}
            </span>
          )}
      </div>
    </div>
  );
};

export default withErrorBoundary(memo(FlightTicket), 'FlightTicket');
